import { useState, useRef, ReactNode } from "react";
import { Tooltip, IconButton, Typography } from "@mui/material";
import { Menu, MenuOpen } from "@mui/icons-material";
import Sidebar from "./Sidebar";

interface BrandHeaderProps {
  /** 좌측 로고 텍스트 (예: "HAIF", "Data CA") */
  title: string;
  /** 우측 뱃지 텍스트 (예: "PRO"). 없으면 뱃지 미표시 */
  badge?: string;
  /** Sidebar 활성 메뉴 상태 */
  activeMenu: string;
  setActiveMenu: (menu: string) => void;
}

/**
 * 상단 브랜드 헤더 + 사이드바 토글.
 * - title / badge props 에 따라 로고·뱃지 표시가 달라짐
 * - hover 시 열리고, 클릭(고정)하면 계속 열린 상태 유지
 */
export default function BrandHeader({ title, badge, activeMenu, setActiveMenu }: BrandHeaderProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [pinned, setPinned] = useState(false);

  const handleMouseEnter = () => {
    if (!pinned) setOpen(true);
  };
  const handleMouseLeave = () => {
    if (!pinned) setOpen(false);
  };
  const handleClick = () => {
    // 클릭으로 고정 토글. 고정 해제 시 닫힘
    const next = !pinned;
    setPinned(next);
    setOpen(next);
  };

  return (
    <div
      ref={containerRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      style={{ position: "fixed", top: 16, left: 16, zIndex: 9999 }}
    >
      <div
        style={{
          background: "linear-gradient(135deg, var(--brand-gradient-from), var(--brand-gradient-to))",
          display: "flex",
          alignItems: "center",
          padding: "10px 18px",
          gap: "8px",
          borderRadius: open ? "12px 12px 0 0" : "12px",
          boxShadow: "var(--brand-shadow)",
          transition: "border-radius 0.15s",
          cursor: "pointer",
        }}
        onClick={handleClick}
      >
        <Tooltip title={pinned ? "클릭 시 메뉴 닫기" : "클릭 시 메뉴 고정"} placement="right">
          <IconButton
            sx={{
              width: "24px",
              borderRadius: 0,
              color: "#fff",
              "&:hover": { backgroundColor: "var(--menu-button-hover)" },
              p: 0,
            }}
          >
            {pinned ? <MenuOpen /> : <Menu />}
          </IconButton>
        </Tooltip>

        <Typography
          sx={{
            fontWeight: 800,
            color: "var(--brand-text)",
            letterSpacing: "1px",
            fontSize: "20px",
          }}
        >
          {title}
        </Typography>

        {badge && (
          <div
            style={{
              backgroundColor: "var(--brand-badge-bg)",
              padding: "2px 10px",
              borderRadius: "6px",
            }}
          >
            <Typography
              sx={{
                fontWeight: 700,
                color: "var(--brand-badge-text)",
                fontSize: "18px",
                letterSpacing: "0.5px",
              }}
            >
              {badge}
            </Typography>
          </div>
        )}
      </div>
      {open && <Sidebar activeMenu={activeMenu} setActiveMenu={setActiveMenu} pinned={pinned} />}
    </div>
  );
}
